import { useMemo, useCallback } from "react";
import {
  ActionButton,
  Button,
  ButtonGroup,
  Content,
  Dialog,
  DialogTrigger,
  Divider,
  Flex,
  Heading,
  Text,
  View,
} from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/storeConfig";
import { CallAccept, CallReject, CallRinging, NewIncomingChat } from "../Icons";
import Emitter from "../../../emitter";
import {
  API_CALLBACK_REJECT,
  API_CALLBACK_UPDATE,
  API_CHAT_REJECT,
  API_CHAT_UPDATE,
} from "../../../constants/events";
import { CHAT_STATES } from "../../../constants/constants";
import { IndividualChat } from "../../../types/chat.type";
import { getInitialsAndColor } from "../../../utils";
import { formatLabel } from "../../../utils/utils";
import { useCustomerAttributes } from "../../../hooks/useCustomerAttributes";

type Props = {
  type: "callback" | "voicemail" | "chat";
};

const AcceptDialog = ({ type }: Props) => {
  const agent = useSelector((state: RootState) => state.agent);
  const callbackOrVoicemail = useSelector((state: RootState) =>
    type === "callback" ? state.callback : state.voicemail
  );
  const chat = useSelector((state: RootState) => state.chat);

  const incomingChat: IndividualChat | null = useMemo(() => {
    if (type !== "chat") return null;
    return (
      chat?.chats.find(
        (c: IndividualChat) => c.self?.state === CHAT_STATES.RINGING
      ) || null
    );
  }, [type, chat?.chats]);

  const data = type === "chat" ? incomingChat : callbackOrVoicemail;
  const isOpen =
    !!data?.id &&
    (type !== "chat" || data?.self?.state === CHAT_STATES.RINGING);

  const customerAttrs = data?.customer?.attributes ?? {};
  const businessAttrs = agent?.businessAttributes ?? [];

  const { groupedAttributes } = useCustomerAttributes({
    customerAttrs,
    businessAttrs,
    type,
  });

  const { initials, avatarColor } = useMemo(() => {
    const first = customerAttrs.FIRSTNAME?.[0] ?? "";
    const last = customerAttrs.LASTNAME?.[0] ?? "";
    const fullInitials = (first + last).trim() || "NA";
    return {
      initials: fullInitials,
      avatarColor: getInitialsAndColor(fullInitials, "color"),
    };
  }, [customerAttrs]);

  const fullName =
    customerAttrs.FIRSTNAME && customerAttrs.LASTNAME
      ? `${customerAttrs.FIRSTNAME} ${customerAttrs.LASTNAME}`
      : "-";

  const emailOrId =
    type === "chat"
      ? customerAttrs.email ?? "-"
      : customerAttrs.CUSTOMER_ADOBE_ID ?? "-";

  const headingIcon = type === "chat" ? <NewIncomingChat /> : <CallRinging />;
  const headingText = `New incoming ${type}`;

  const rejectHandler = useCallback(() => {
    if (!data?.id) return;
    if (type === "chat") {
      Emitter.emit(API_CHAT_REJECT, data.id);
    } else {
      Emitter.emit(API_CALLBACK_REJECT, {
        body: {
          participantId: data.self?.id,
          conversationId: data.id,
          state: "disconnected",
        },
      });
    }
  }, [type, data]);

  const acceptHandler = useCallback(() => {
    if (!data?.id) return;
    const payload = {
      body: {
        participantId: data.self?.id,
        conversationId: data.id,
        state: "connected",
      },
    };
    Emitter.emit(
      type === "chat" ? API_CHAT_UPDATE : API_CALLBACK_UPDATE,
      payload
    );
  }, [type, data]);

  if (!isOpen) return null;

  const renderAttributeGroup = (group: any[], idx: number) => (
    <View key={`group-${idx}`} UNSAFE_className="call-incoming-attributes">
      <Flex direction="row" wrap gap={40}>
        {group.map((attr, subIdx) => (
          <Flex
            key={`attr-${idx}-${subIdx}`}
            direction="column"
            UNSAFE_style={{ flex: 1, minWidth: "45%", maxWidth: "45%" }}
          >
            <Text UNSAFE_className="call-content-heading">
              {formatLabel(attr.attributeName)}
            </Text>
            <Text
              UNSAFE_className="call-content-descritpion"
              UNSAFE_style={{ wordBreak: "break-word" }}
            >
              {customerAttrs[attr.attributeName] ?? "-"}
            </Text>
          </Flex>
        ))}
      </Flex>
    </View>
  );

  return (
    <DialogTrigger type="modal" mobileType="modal" defaultOpen>
      <ActionButton isHidden isQuiet />
      {() => (
        <Dialog size="S" UNSAFE_className="call-incoming-dialog">
          <Heading level={4}>
            <Flex direction="row" gap="size-100" alignItems="center">
              <Text UNSAFE_className="call-heading-icon">{headingIcon}</Text>
              <Text UNSAFE_className="call-heading-text">{headingText}</Text>
            </Flex>
          </Heading>
          <Divider
            marginBottom="size-0"
            UNSAFE_className="call-incoming-divider"
          />
          <Content maxWidth="100vw" marginTop="15px">
            <Flex direction="column" gap="size-200" width="100%">
              <Flex direction="row" width="100%">
                <Flex direction="column" width="45%">
                  <Flex>
                    <View UNSAFE_className="call-history-avatar">
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        UNSAFE_className="agent-avatar"
                        UNSAFE_style={{
                          backgroundColor: avatarColor as string,
                          borderRadius: "50%",
                        }}
                      >
                        <Text UNSAFE_className="call-monitor-agent-text">
                          {initials}
                        </Text>
                      </Flex>
                    </View>
                    <Flex direction="column">
                      <Text UNSAFE_className="call-content-phone-heading">
                        {fullName}
                      </Text>
                      <Text UNSAFE_className="call-content-email-descritpion">
                        {emailOrId}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              {groupedAttributes.map(renderAttributeGroup)}
            </Flex>
          </Content>
          <ButtonGroup>
            <Button
              variant="negative"
              onPress={rejectHandler}
              UNSAFE_className="call-reject-btn"
              autoFocus={false}
            >
              {type !== "chat" && (
                <span className="reject-btn-icon">
                  <CallReject />
                </span>
              )}
              <Text UNSAFE_style={{ display: "contents" }}>Reject</Text>
            </Button>
            <Button
              variant="accent"
              onPress={acceptHandler}
              UNSAFE_className="call-accept-btn"
              autoFocus={false}
            >
              {type !== "chat" && (
                <span className="calling-btn-icon">
                  <CallAccept />
                </span>
              )}
              <Text UNSAFE_style={{ display: "contents" }}>Accept</Text>
            </Button>
          </ButtonGroup>
        </Dialog>
      )}
    </DialogTrigger>
  );
};

export default AcceptDialog;
